import { VendorConfiguration, VENDOR_CONFIGURATIONS, VendorType } from "../types/VendorConfiguration";

/**
 * Interface for vendor-specific configuration strategies
 */
export interface VendorStrategy {
    /**
     * Gets the vendor configuration
     */
    getConfiguration(): VendorConfiguration;

    /**
     * Determines if this strategy applies to the given vendor control ID
     */
    matches(vendorControlId: string): boolean;

    /**
     * Gets the vendor type
     */
    getVendorType(): VendorType;
}

/**
 * Abstract base class for vendor strategies
 */
abstract class BaseVendorStrategy implements VendorStrategy {
    protected readonly vendorType: VendorType;
    protected readonly configuration: VendorConfiguration;

    constructor(vendorType: VendorType) {
        this.vendorType = vendorType;
        this.configuration = VENDOR_CONFIGURATIONS[vendorType];
    }

    getConfiguration(): VendorConfiguration {
        return this.configuration;
    }

    matches(vendorControlId: string): boolean {
        return vendorControlId.startsWith(this.configuration.prefix);
    }

    getVendorType(): VendorType {
        return this.vendorType;
    }
}

// Concrete strategy implementations
class OmnittracsStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.OMNITRACS); }
}

class PegasusStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.PEGASUS); }
}

class PlatformScienceStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.PLATFORM_SCIENCE); }
}

class BlueTreeStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.BLUE_TREE); }
}

class ForwardThinkingStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.FORWARD_THINKING); }
}

class IbrightStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.IBRIGHT); }
}

class MavenStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.MAVEN); }
}

class SymphonyStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.SYMPHONY); }
}

class TranztecStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.TRANZTEC); }
}

class SamsaraStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.SAMSARA); }
}

class McLeodStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.MCLEOD); }
}

class CarrierWebStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.CARRIER_WEB); }
}

class FleetLocateStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.FLEET_LOCATE); }
}

class PeoplenetStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.PEOPLENET); }
}

class PedigreeStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.PEDIGREE); }
}

class QualcommWorkflowStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.QUALCOMM_WORKFLOW); }
}

class QualcommStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.QUALCOMM); }
}

class TeletracStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.TELETRAC); }
}

class DriverTechStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.DRIVER_TECH); }
}

class XRSStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.XRS); }
}

class KeepTruckinStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.KEEP_TRUCKIN); }
}

class RandDCStrategy extends BaseVendorStrategy {
    constructor() { super(VendorType.RAND_DC); }
}

/**
 * Factory class for creating vendor strategies
 */
export class VendorStrategyFactory {
    private static strategies: VendorStrategy[] = [
        new OmnittracsStrategy(),
        new PegasusStrategy(),
        new PlatformScienceStrategy(),
        new BlueTreeStrategy(),
        new ForwardThinkingStrategy(),
        new IbrightStrategy(),
        new MavenStrategy(),
        new SymphonyStrategy(),
        new TranztecStrategy(),
        new SamsaraStrategy(),
        new McLeodStrategy(),
        new CarrierWebStrategy(),
        new FleetLocateStrategy(),
        new PeoplenetStrategy(),
        new PedigreeStrategy(),
        new QualcommWorkflowStrategy(),
        new QualcommStrategy(),
        new TeletracStrategy(),
        new DriverTechStrategy(),
        new XRSStrategy(),
        new KeepTruckinStrategy(),
        new RandDCStrategy()
    ];

    /**
     * Gets the appropriate strategy for a vendor control ID
     */
    static getStrategy(vendorControlId: string): VendorStrategy | null {
        if (!vendorControlId) {
            return null;
        }

        return this.strategies.find(strategy => strategy.matches(vendorControlId)) || null;
    }

    /**
     * Gets all available strategies
     */
    static getAllStrategies(): VendorStrategy[] {
        return [...this.strategies];
    }
}
